import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ApiService,
    GraphQLResponse,
    Mk2Request,
    Mk2Response,
    Pagination
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    ApprovalResponse,
    Money,
    InvoiceFundingStatus2,
    CreditMemoResponse,
    PayablesInput,
    PayablesResponse
} from '../../model/models';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import { isUndefined } from 'lodash';
import { startWith } from 'rxjs/operators';
import { mapPayableStatusLegacyToInvoiceFundingStatus } from '../../helpers/status-mapping';

export interface InvoiceDetailsItemResponse {
    amount?: Money;
    businessUnitKey?: string;
    businessUnitName?: string;
    dueDate?: string;
    finalApprovedAmount?: Money;
    fundingStatus?: InvoiceFundingStatus2;
    invoiceDate?: string;
    invoiceKey?: string;
    invoiceNumber?: string;
    invoiceTerms?: number;
    sellerKey?: string;
    sellerName?: string;
    status?: InvoiceFundingStatus2;
}

export class InvoiceDetailsRequest extends Mk2Request {
    isCreditMemo?: boolean;
    buyerKeysFilter?: string[];
    invoiceKey?: string;
    message?: string;
    ok?: boolean;

    constructor(initial?: Partial<InvoiceDetailsRequest>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

export class InvoiceDetailsResponse extends Mk2Response {
    pagination?: Pagination;
    rows?: InvoiceDetailsItemResponse[];
    totalCount?: number;

    constructor(initial?: Partial<InvoiceDetailsResponse>) {
        super();
        if (initial != null) {
            Object.keys(initial).forEach((key) => {
                this[key] = initial[key];
            });
        }
    }
}

@Injectable({ providedIn: 'root' })
export class ListInvoiceDetailsService extends ApiService<
    InvoiceDetailsRequest,
    InvoiceDetailsResponse
> {
    public requestType = InvoiceDetailsRequest;
    public responseType = InvoiceDetailsResponse;
    public endpointName = 'BuyerAccount/InvoiceList/ListInvoiceDetails';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'BuyerAccount/InvoiceList/ListInvoiceDetails';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }
    public requestMapper(req: InvoiceDetailsRequest): { query: string } | InvoiceDetailsRequest {
        if (this._isPayLaterEnabled) {
            const { invoiceKey, buyerKeysFilter } = req;
            const input: PayablesInput = {
                companyFilter: { buyerIds: buyerKeysFilter },
                payableIds: [+invoiceKey]
            };
            const amount = { cents: true, currencyCode: true };
            const relationship = { supplier: { id: true, company: { name: true } } };
            const query = jsonToGraphQLQuery({
                payables: {
                    __args: { input },
                    rows: {
                        creditMemos: {
                            id: true,
                            date: true,
                            creditMemoNumber: true,
                            relationship,
                            amount
                        },
                        approvals: {
                            amount,
                            originalDueDate: true,
                            statusLegacy: true,
                            invoice: {
                                id: true,
                                date: true,
                                terms: true,
                                invoiceNumber: true,
                                relationship
                            }
                        }
                    }
                }
            });

            return { query: `{${query}}` };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<ApprovalResponse | CreditMemoResponse> | InvoiceDetailsResponse
    ): InvoiceDetailsResponse {
        if (this._isPayLaterEnabled) {
            const responseData = (res as GraphQLResponse<PayablesResponse>).data.payables.rows[0];
            const approval = responseData.approvals[0];
            const creditMemo = responseData.creditMemos[0];

            if (isUndefined(creditMemo)) {
                const { invoice } = approval;
                const { supplier } = invoice.relationship;
                return new InvoiceDetailsResponse({
                    ok: true,
                    totalCount: 1,
                    rows: [
                        <InvoiceDetailsItemResponse>{
                            invoiceDate: invoice.date,
                            invoiceNumber: invoice.invoiceNumber,
                            status: mapPayableStatusLegacyToInvoiceFundingStatus(
                                approval.statusLegacy
                            ),
                            fundingStatus: mapPayableStatusLegacyToInvoiceFundingStatus(
                                approval.statusLegacy
                            ),
                            amount: approval.amount,
                            finalApprovedAmount: approval.amount,
                            dueDate: approval.originalDueDate,
                            sellerKey: supplier.id,
                            sellerName: supplier.company.name
                        }
                    ]
                });
            }

            const creditMemoAmount = creditMemo.amount;
            const { supplier } = creditMemo.relationship;
            return new InvoiceDetailsResponse({
                ok: true,
                totalCount: 1,
                rows: [
                    <InvoiceDetailsItemResponse>{
                        invoiceDate: creditMemo.date,
                        invoiceNumber: creditMemo.creditMemoNumber,
                        amount: creditMemoAmount,
                        finalApprovedAmount: creditMemoAmount,
                        sellerKey: supplier.id,
                        sellerName: supplier.company.name
                    }
                ]
            });
        }
        return res as InvoiceDetailsResponse;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
